import { boardCategoryName } from './categoryNames';
import { CASH_FLOW_ROLE_IDS } from '@steward-app/steward-types';

/**
 * Shared pieces of the cash flow boards (steward-app#2593, docs/39 §8.6): the response shapes of
 * `GET /cash-flow/summary` and `GET /cash-flow/entity/{key}` as `handlers/cashFlow.ts` returns
 * them (read 2026-11-02), the viewer's level, and the display names the SPA owns.
 */

export type CashFlowLevel = 'none' | 'totals' | 'every';

export const LEVEL_LABEL: Record<CashFlowLevel, string> = {
  none: 'Nothing',
  totals: 'Totals by category',
  every: 'Every transaction',
};

/**
 * The viewer's own level from their membership roles: the highest of the family roles and the two
 * firm roles (an admin who is also an advisor holds a firm role and the façade serves it).
 */
export function levelFromRoles(roleIds: readonly string[]): CashFlowLevel {
  if (roleIds.includes(CASH_FLOW_ROLE_IDS.FAMILY_DETAIL) || roleIds.includes(CASH_FLOW_ROLE_IDS.FIRM_DETAIL)) return 'every';
  if (roleIds.includes(CASH_FLOW_ROLE_IDS.FAMILY_TOTALS) || roleIds.includes(CASH_FLOW_ROLE_IDS.FIRM_TOTALS)) return 'totals';
  return 'none';
}

export interface InOut { inflowCents: number; outflowCents: number }
export interface CategoryTotals extends InOut { internalCents: number }

export type SummaryMonth =
  | { month: string; computed: false }
  | {
    month: string; computed: true; inflowCents: number; outflowCents: number; internalCents: number;
    openQuestions: number; byEntity: Record<string, InOut>; byCategory: Record<string, CategoryTotals>;
    computedAt: string | null;
    /** True: the month holds no transaction at all. null: a row from before the count existed (unknown). */
    empty?: boolean | null;
  };

export type EntityMonth =
  | { month: string; computed: false }
  | { month: string; computed: true; inflowCents: number; outflowCents: number; empty?: boolean | null };

export type SummaryResponse =
  | { status: 'off' }
  | { status: 'on'; from: string; to: string; months: SummaryMonth[] };

export type EntityResponse =
  | { status: 'off' }
  | { status: 'on'; key: string; from: string; to: string; months: EntityMonth[] };

/** The `unassigned` key `byEntity` uses for money in an account no entity claims. */
export const UNASSIGNED_KEY = 'unassigned';

export const netCents = (t: InOut): number => t.inflowCents - t.outflowCents;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** `2026-10` to `Oct`. Pure string work: a `Date` would shift the month in some timezones. */
export function shortMonth(month: string): string {
  return (MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month).slice(0, 3);
}

/** `2026-10` to `October 2026`. */
export function longMonth(month: string): string {
  const name = MONTH_NAMES[Number(month.slice(5, 7)) - 1];
  return name ? `${name} ${month.slice(0, 4)}` : month;
}

/**
 * The month a board opens on: the newest computed month that holds a transaction, else the newest
 * computed month, else the newest month. A month with no transactions (this one, just begun) is
 * not where a family wants to land.
 */
export function defaultMonth(months: readonly { month: string; computed: boolean; empty?: boolean | null }[]): string | undefined {
  for (let i = months.length - 1; i >= 0; i--) if (months[i]!.computed && months[i]!.empty !== true) return months[i]!.month;
  for (let i = months.length - 1; i >= 0; i--) if (months[i]!.computed) return months[i]!.month;
  return months[months.length - 1]?.month;
}

/** Category rows by display name: keys that share a name (an unknown key reads Other) merge into one row. */
export function mergeByLabel(byCategory: Record<string, CategoryTotals>): [string, CategoryTotals][] {
  const out = new Map<string, CategoryTotals>();
  for (const [key, t] of Object.entries(byCategory)) {
    const name = boardCategoryName(key);
    const cur = out.get(name) ?? { inflowCents: 0, outflowCents: 0, internalCents: 0 };
    out.set(name, {
      inflowCents: cur.inflowCents + t.inflowCents,
      outflowCents: cur.outflowCents + t.outflowCents,
      internalCents: cur.internalCents + t.internalCents,
    });
  }
  return [...out.entries()];
}
